import { datadogRum, RumPlugin } from "@datadog/browser-rum";
import { useClientApiKeys } from "@hooks/useClientSettings";
import { appVersion, envMode, apiUrlOriginacaoDefault } from "@settings";
import { useEffect, useMemo, useRef } from "react";
import useAuth from "@hooks/useAuth";
import { useSessionReplay } from "@hooks/useSessionReplay";

export const DataDogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { dataDog } = useClientApiKeys();
  const { user } = useAuth();
  const hasInitialized = useRef(false);
  useSessionReplay(hasInitialized.current);

  const plugin = useMemo<RumPlugin>(
    () => ({
      name: "custom-actions",
      onInit({ publicApi }) {
        publicApi.addAction("my_custom_action");
      },
    }),
    [],
  );

  useEffect(() => {
    const { applicationId, clientToken, serviceName } = dataDog ?? {};

    if (
      hasInitialized.current ||
      !applicationId ||
      !clientToken ||
      !serviceName
    )
      return;

    datadogRum.init({
      applicationId,
      clientToken,
      site: "datadoghq.com",
      service: serviceName,
      env: envMode,
      version: appVersion,

      sessionSampleRate: 10,
      sessionReplaySampleRate: 5,

      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,

      defaultPrivacyLevel: "mask-user-input",
      plugins: [plugin],
      allowedTracingUrls: [apiUrlOriginacaoDefault],
    });

    hasInitialized.current = true;
  }, [dataDog, plugin]);

  useEffect(() => {
    if (user?.id) {
      datadogRum.setUser({
        id: String(user.id),
        name: user.name ?? undefined,
        identifier: user.identifier ?? undefined,
      });
    }
  }, [user]);

  return <>{children}</>;
};
